import PropTypes from 'prop-types';
import React from 'react';
import { LightBoxWrapper } from '../LightBoxWrapper.js';
import { bubblyFillCanvas, resetCanvas, tinyCardFillCanvas, HLHMCanvas } from './canvasFiller.js';
import { SHARE_TOOLS, trackShareToolClick } from '../shareToolUtils.js';


/* global dcAnalytics */

const QuoteBoxRadioButton = ({checked, value, onClick, id, labelText, disabled}) => {
    return (
        <li role="radio" tabIndex="0" className="js-click-first-child-on-enter">
            <input type="radio"
                   id={id}
                   checked={checked}
                   className="tab-select"
                   value={value}
                   onClick={onClick}
                   autoFocus="autofocus"
                   disabled={disabled}
            />
            <label htmlFor={id} onClick={onClick}>{labelText}</label>
        </li>
    );
}
QuoteBoxRadioButton.propTypes = {
    checked: PropTypes.bool.isRequired,
    disabled: PropTypes.bool.isRequired,
    value: PropTypes.string.isRequired,
    onClick: PropTypes.func.isRequired,
    id: PropTypes.string.isRequired,
    labelText: PropTypes.string.isRequired,
}

const VERSIONS = {
    BUBBLY: 'bubbly',
    CARD: 'card',
    HLHM: 'hlhm',
};

/**
 * Like ClickToCopy, but instead of showing a temporary tooltip after being clicked, just replace the link with some
 * text saying the copy went through.
 */
const OneTimeClickToCopy = ({ projectId, url }) => {
    const [clicked, setClicked] = React.useState(false);

    const onClick = (e) => {
        e.preventDefault();

        navigator.clipboard.writeText(url);
        // Note dependency on window.pageName, a global generally set in JSPs.
        dcAnalytics.trackShareToolClick(window.pageName, url, 'instagram');
        trackShareToolClick(projectId, SHARE_TOOLS.INSTAGRAM);

        setClicked(true);
    }

    if (clicked) {
        return <span className='copy-success'>Link copied!</span>;
    } else {
        return <a onClick={onClick} href='#'>Click here to copy your unique project link.</a>;
    }
}
OneTimeClickToCopy.propTypes = {
    url: PropTypes.string.isRequired,
};

// Note: Instagram stories require a 9:16 ratio. If you want to change these, make sure they remain matching multiples
// of those two numbers!
const CANVAS_HEIGHT = 432; // 400;
const CANVAS_WIDTH = 243; // 225;

export const InstagramSharetool = ({urlToShare, isOpen, setIsOpen, projectData}) => {
    // Hooks at the top
    const [version, setVersion] = React.useState(VERSIONS.BUBBLY);
    const [ready, setReady] = React.useState(false);
    const canvasRef = React.useRef();

    // Update the canvas in the first render and also every time `version` changes.
    //
    // I think it's safe to use `useEffect` instead of `useLayoutEffect` here because a React-render won't change
    // anything _within_ this canvas.
    React.useEffect(() => {
        const { classroomPhotoUrl, profilePhotoUrl, teacherName, costToComplete, totalPrice, fulfillmentTrailer } = projectData;

        const onSuccess = () => { setReady(true); };

        // TODO Consider forwarding the projectData abstraction in.
        if (version === VERSIONS.BUBBLY) {
            bubblyFillCanvas(canvasRef.current, classroomPhotoUrl, profilePhotoUrl, teacherName, fulfillmentTrailer, costToComplete, totalPrice, onSuccess);
        } else if (version === VERSIONS.CARD) {
            tinyCardFillCanvas(canvasRef.current, classroomPhotoUrl, profilePhotoUrl, teacherName, fulfillmentTrailer, costToComplete, totalPrice, onSuccess);
        } else{
            HLHMCanvas(canvasRef.current, classroomPhotoUrl, profilePhotoUrl, teacherName, fulfillmentTrailer, costToComplete, totalPrice, onSuccess);
        }

        // When resetting this effect (to swap versions or tear down the component), reset the canvas and mark the component as non-ready.
        return () => {
            // `canvasRef.current` is null during teardown step - don't bother resetting in that case.
            if (canvasRef.current) {
                resetCanvas(canvasRef.current);
            }
            
            setReady(false);
        }
    }, [
        version,
        canvasRef,
        setReady,
        projectData.classroomPhotoUrl,
        projectData.profilePhotoUrl,
        projectData.teacherName,
        projectData.costToComplete,
        projectData.totalPrice,
        projectData.fulfillmentTrailer,
    ]);

    // Early-return-conditions: must come after hooks
    if (!isOpen) {
        return null;
    }

    // Fancy wrapped-Hook functions: can happen after early-return.
    const unmountHandler = () => {
        setIsOpen(false);
    }

    const makeClickHandler = (newValue) => {
        return (e) => {
            // We need both of these to prevent this click /within/ a ProjectCard from closing the lightbox.
            e.preventDefault();
            e.stopPropagation();

            // No-op if we're in mid-canvas-update. Otherwise async canvas-update-functions from Version 1 might fire
            // after the switch to Version 2. (Disabling the inputs is not enough - click handlers will fire regardless)
            if (!ready) {
                return;
            }

            setVersion(newValue);
        }
    }

    const onBubblyClick = makeClickHandler(VERSIONS.BUBBLY);
    const onCardClick = makeClickHandler(VERSIONS.CARD);
    const onHLHMClick = makeClickHandler(VERSIONS.HLHM);

    return (
        <LightBoxWrapper viewportPositioned={true} unMountHandler={unmountHandler}>
            <h2>Choose an image to share</h2>

            <div>
                <ul className="segmented-control pointer ig-segmented-control">
                    <QuoteBoxRadioButton disabled={!ready} id="instagram-template-bubbly" checked={version === VERSIONS.BUBBLY} labelText={'Overview'} value={VERSIONS.BUBBLY} onClick={onBubblyClick} />
                    <QuoteBoxRadioButton disabled={!ready} id="instagram-template-card" checked={version === VERSIONS.CARD} labelText={'Progress'} value={VERSIONS.CARD} onClick={onCardClick} />
                    <QuoteBoxRadioButton disabled={!ready} id="instagram-template-hlhm" checked={version === VERSIONS.HLHM} labelText={'Hispanic & Latinx Heritage Month'} value={VERSIONS.HLHM} onClick={onHLHMClick} />
                </ul>

                { /* Note: this `style` is nontrivial; it controls layout-size. canvas.height and canvas.width affect
                both layout-size and resolution; they will be set _based on this layout-size_. */ }
                <canvas style={{height: CANVAS_HEIGHT, width: CANVAS_WIDTH}} ref={canvasRef} />
            </div>


            <ol className="ig-instructions">
                <li><OneTimeClickToCopy projectId={projectData.projectId} url={urlToShare} /></li>
                <li><strong>Take a screenshot</strong> of the image.</li>
                <li>Open your Instagram app and <strong>create a story</strong> using the screenshot. (Pro tip: pinch to zoom in so the image fills the screen.)</li>
                <li>Make sure to <strong>add a "link" sticker</strong> and paste in yours!</li>
            </ol>

        </LightBoxWrapper>
    );
}
const projectDataShape = PropTypes.shape({
    classroomPhotoUrl: PropTypes.string,
    profilePhotoUrl: PropTypes.string,
    teacherName: PropTypes.string.isRequired,
    costToComplete: PropTypes.number,
    totalPrice: PropTypes.number,
    fulfillmentTrailer: PropTypes.string.isRequired,
});
InstagramSharetool.propTypes = {
    urlToShare: PropTypes.string.isRequired,
    isOpen: PropTypes.bool.isRequired,
    setIsOpen: PropTypes.func.isRequired,
    projectData: projectDataShape.isRequired,
}
